import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CommonDataTable from '../../../components/CommonDataTable';
import { fetchItemInventory, refreshWMSAPI } from '../../../services/item/item';
import { lottableAt, lottableDateAt, lottableLabelAt } from '../inventoryFieldValue';

const compareOwnerThenSku = (left, right) => {
  const byOwner = String(left?.owner ?? '').localeCompare(String(right?.owner ?? ''), undefined, { numeric: true, sensitivity: 'base' });
  if (byOwner !== 0) return byOwner;
  return String(left?.item ?? '').localeCompare(String(right?.item ?? ''), undefined, { numeric: true, sensitivity: 'base' });
};

const LOT_SUMMARY_CAT = 'lot_summary';

const fetchAllInventoryRows = async (filters = []) => {
  const size = 100;
  let pageNo = 0;
  let collected = [];
  let total = 0;

  do {
    const res = await fetchItemInventory(pageNo, size, filters, LOT_SUMMARY_CAT);
    const list = Array.isArray(res?.data) ? res.data : [];
    total = Number(res?.totalItems ?? list.length);
    collected = collected.concat(list);
    pageNo += 1;
    if (list.length === 0) break;
  } while (collected.length < total && pageNo < 30);

  return collected;
};

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
};

const lottableLabel = (item, index) =>
  firstValue(item[`lottable${String(index).padStart(2, '0')}Label`], lottableLabelAt(item, index));

const lottableValue = (item, index) =>
  firstValue(item[`lottable${String(index).padStart(2, '0')}Value`], lottableAt(item, index));

const mapLotSummaryRow = (item, index) => ({
  id: item._id || `${item.skuId || 'item'}-${item.lot || index}`,
  lot: firstValue(item.lot, item.lotNo, item.batchNum, item.batch, item.batchLot),
  owner: item.owner ?? '',
  item: item.skuId ?? item.item ?? '',
  description: firstValue(item.description, item.itemName),
  onHand: firstValue(item.stockOnHand, item.onHand),
  available: firstValue(item.availableQty, item.available),
  status: firstValue(item.status, item.wmsStatus),
  allocated: item.allocated ?? '',
  picked: firstValue(item.qtyPicked, item.picked, item.pickedQty),
  onHold: item.onHold ?? '',
  grossWeight: firstValue(item.grossWeight, item.gross, item.grossWgt),
  netWeight: firstValue(item.netWeight, item.net, item.netWgt),
  tareWeight: firstValue(item.tareWeight, item.tare, item.tareWgt),
  lottable01Label: lottableLabel(item, 1),
  lottable01Value: lottableValue(item, 1),
  lottable02Label: lottableLabel(item, 2),
  lottable02Value: lottableValue(item, 2),
  lottable03Label: lottableLabel(item, 3),
  lottable03Value: lottableValue(item, 3),
  manufacturingDate: firstValue(item.manufacturingDate, lottableDateAt(item, 4)),
  expirationDate: firstValue(item.expirationDate, lottableDateAt(item, 5)),
  lottable06Label: lottableLabel(item, 6),
  lottable06Value: lottableValue(item, 6),
  lottable07Label: lottableLabel(item, 7),
  lottable07Value: lottableValue(item, 7),
  lottable08Label: lottableLabel(item, 8),
  lottable08Value: lottableValue(item, 8),
  lottable09Label: lottableLabel(item, 9),
  lottable09Value: lottableValue(item, 9),
  lottable10Label: lottableLabel(item, 10),
  lottable10Value: lottableValue(item, 10),
  lottable11Label: lottableLabel(item, 11),
  lottable11Value: lottableValue(item, 11),
  lottable12Label: lottableLabel(item, 12),
  lottable12Value: lottableValue(item, 12),
});

const rawColumns = [
  { field: 'lot', headerName: 'Lot', width: 140 },
  { field: 'owner', headerName: 'Owner', width: 140 },
  { field: 'item', headerName: 'SKU ID', width: 160 },
  { field: 'description', headerName: 'Description', width: 220 },
  { field: 'onHand', headerName: 'On Hand', width: 110, type: 'number' },
  { field: 'available', headerName: 'Available', width: 110, type: 'number' },
  { field: 'status', headerName: 'Status', width: 120 },
  { field: 'allocated', headerName: 'Allocated', width: 110, type: 'number' },
  { field: 'picked', headerName: 'Picked', width: 110, type: 'number' },
  { field: 'onHold', headerName: 'On Hold', width: 110, type: 'number' },
  { field: 'grossWeight', headerName: 'Gross Weight', width: 130, type: 'number' },
  { field: 'netWeight', headerName: 'Net Weight', width: 130, type: 'number' },
  { field: 'tareWeight', headerName: 'Tare Weight', width: 130, type: 'number' },
  { field: 'lottable01Label', headerName: 'Lottable 01(Label)', width: 160 },
  { field: 'lottable01Value', headerName: 'Lottable 01(Value)', width: 160 },
  { field: 'lottable02Label', headerName: 'Lottable02(Label)', width: 160 },
  { field: 'lottable02Value', headerName: 'Lottable 02(Value)', width: 160 },
  { field: 'lottable03Label', headerName: 'Lottable03(Label)', width: 160 },
  { field: 'lottable03Value', headerName: 'Lottable 03(Value)', width: 160 },
  { field: 'manufacturingDate', headerName: 'Manufacturing Date', width: 170 },
  { field: 'expirationDate', headerName: 'Expiration Date', width: 160 },
  { field: 'lottable06Label', headerName: 'Lottable 06(Label)', width: 160 },
  { field: 'lottable06Value', headerName: 'Lottable 06(Value)', width: 160 },
  { field: 'lottable07Label', headerName: 'Lottable 07(Label)', width: 160 },
  { field: 'lottable07Value', headerName: 'Lottable 07(Value)', width: 160 },
  { field: 'lottable08Label', headerName: 'Lottable 08(Label)', width: 160 },
  { field: 'lottable08Value', headerName: 'Lottable 08(Value)', width: 160 },
  { field: 'lottable09Label', headerName: 'Lottable 09(Label)', width: 160 },
  { field: 'lottable09Value', headerName: 'Lottable 09(Value)', width: 160 },
  { field: 'lottable10Label', headerName: 'Lottable 10(Label)', width: 160 },
  { field: 'lottable10Value', headerName: 'Lottable 10(Value)', width: 160 },
  { field: 'lottable11Label', headerName: 'Lottable 11(Label)', width: 160 },
  { field: 'lottable11Value', headerName: 'Lottable 11(Value)', width: 160 },
  { field: 'lottable12Label', headerName: 'Lottable 12(Label)', width: 160 },
  { field: 'lottable12Value', headerName: 'Lottable 12(Value)', width: 160 },
];

export default function LotAndItemList() {
  const [tableRows, setTableRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const sortedCacheRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchHandler = useCallback(async (pageNo, pageSizeNo, filters = [], options = {}) => {
    const filterKey = JSON.stringify(Array.isArray(filters) ? filters : []);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      if (options.refresh || !sortedCacheRef.current || sortedCacheRef.current.filterKey !== filterKey) {
        const list = await fetchAllInventoryRows(filters);
        if (requestId !== requestIdRef.current) return;
        const rows = list.map(mapLotSummaryRow);
        rows.sort(compareOwnerThenSku);
        sortedCacheRef.current = { filterKey, rows };
      }

      const sorted = sortedCacheRef.current.rows;
      const start = pageNo * pageSizeNo;
      setTableRows(sorted.slice(start, start + pageSizeNo));
      setTotalCount(sorted.length);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error fetching Lot and Item data:', error);
    }
  }, []);

  useEffect(() => {
    fetchHandler(page, pageSize, appliedFilters);
  }, [fetchHandler, page, pageSize, appliedFilters]);

  const handleDataRefresh = useCallback((payload = {}) => {
    const nextFilters = Array.isArray(payload.filters) ? payload.filters : [];
    const nextSize = Number(payload.pageSize);
    setAppliedFilters(nextFilters);
    if (Number.isFinite(nextSize) && nextSize > 0 && nextSize !== pageSize) {
      setPageSize(nextSize);
    }
    setPage(0);
  }, [pageSize]);

  const handleSyncWms = useCallback(async () => {
    if (syncing) return;

    try {
      setSyncing(true);
      await refreshWMSAPI();
      sortedCacheRef.current = null;
      await fetchHandler(page, pageSize, appliedFilters, { refresh: true });
    } catch (error) {
      console.error('WMS Sync Error:', error);
      alert(
        error?.response?.data?.message ||
        'Failed to sync item inventory from WMS'
      );
    } finally {
      setSyncing(false);
    }
  }, [syncing, fetchHandler, page, pageSize, appliedFilters]);

  const allPossibleColumns = useMemo(() => rawColumns, []);
  const initialVisible = useMemo(() => allPossibleColumns.map((column) => column.field), [allPossibleColumns]);

  return (
    <CommonDataTable
      id="LotAndItemLottables"
      allPossibleColumns={allPossibleColumns}
      initialVisible={initialVisible}
      initialRows={tableRows}
      height={520}
      rowsPerPageOptions={[10, 25, 50, 100]}
      exportImportPath="ItemInventory"
      exportQueryParams={{ cat: LOT_SUMMARY_CAT }}
      forItems
      forInventory
      enableInlineColumnFilters
      showSyncWms
      onSyncWms={handleSyncWms}
      page={page}
      pageSize={pageSize}
      rowCount={totalCount}
      onPageChange={(newPage) => setPage(newPage)}
      onPageSizeChange={(newSize) => {
        setPageSize(newSize);
        setPage(0);
      }}
      onDataRefresh={handleDataRefresh}
      onManualRefresh={(payload) => {
        sortedCacheRef.current = null;
        return fetchHandler(
          payload?.page ?? page,
          payload?.pageSize ?? pageSize,
          payload?.filters ?? appliedFilters,
          { refresh: true }
        );
      }}
    />
  );
}
